import useKeyboardNavigation from "../hooks/useKeyboardNavigation";
import usePdfFitToPage from "../hooks/usePdfFitToPage";
import OptimizedGridLines from "./OptimizedGridLines";
import PdfNavigationBar from "./PdfNavigationBar";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  useTheme,
} from "@mui/material";
import React, { memo } from "react";

// Helper function to get highlight colors for PDF - yellow for all words, blue for selected
const getPdfHighlightColor = (isSelected = false) => {
  if (isSelected) {
    return {
      backgroundColor: "rgba(33, 150, 243, 0.7)", // Bright blue for selected word
      borderColor: "#2196f3",
      shadowColor: "rgba(33, 150, 243, 0.8)",
    };
  }

  // Default yellow highlighting for all words
  return {
    backgroundColor: "rgba(255, 193, 7, 0.6)", // Brighter yellow
    borderColor: "#ffc107",
    shadowColor: "none",
  };
};

const PdfViewerPanel = ({
  // Panel state
  pdfPanelExpanded,
  sidebarWidth,

  // PDF state
  isPdfLoading,
  isPdfError,
  pdfScale,
  setPdfScale,
  currentPage,
  setCurrentPage,
  totalPages,
  pdfDoc,

  // Refs
  highlightContainerRef,
  canvasRef,
  pdfPageDimensions,

  // Table data
  selectedTable,
  tableData,
  showGrid,
  setShowGrid,
  dynamicGridLines,
  deletedRows,
  deletedColumns,

  // Highlighting
  highlights,

  // Drawing state
  drawingMode,
  setDrawingMode,
  drawingShape,
  polygonPoints,
  previewWords,
  showDrawingConfirmation,
  showGridLineConfirmation,
  setShowGridLineConfirmation,
  isPlacingGridLine,
  isDraggingGridLine,
  placingGridLineType,
  draggingGridLine,
  gridLineDragMode,
  pendingShapeWords,
  pendingNewGridLines,

  // Table Structure Controls
  startAddNewRow,
  startAddNewColumn,
  cancelGridLinePlacement,

  // Recovery Controls
  setRecoveryMenuAnchor,
  setShowRecoveryMenu,

  // Event handlers
  startDrawing,
  continueDrawing,
  finishDrawing,
  cancelDrawing,
  finishPolygon,
  handleGridLinePlacement,
  continueGridLineDrag,
  startGridLineDrag,
  stopGridLineDrag,
  handlePdfClick,
  showGridLineMoved,
  confirmGridLineMoved,
  cancelGridLineMoved,
}) => {
  const theme = useTheme();
  const borderColor = theme.brand.gray.gainsboro;
  // Initialize fit to page hook
  const { handleFitToPage } = usePdfFitToPage(
    canvasRef,
    pdfPageDimensions,
    setPdfScale
  );

  // Initialize keyboard navigation hook
  useKeyboardNavigation({
    pdfPanelExpanded,
    currentPage,
    totalPages,
    setCurrentPage,
  });

  if (!pdfPanelExpanded) {
    return null;
  }

  return (
    <Box
      sx={{
        flexBasis: `${sidebarWidth}%`,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        p: 2,
        minWidth: 0, // Prevent flex item from overflowing
      }}
    >
      <Card sx={{ flex: 1, overflow: "hidden" }}>
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "auto",
            backgroundColor: "#f5f5f5",
            p: 2,
          }}
        >
          {/* PDF Navigation Bar - positioned at top of canvas */}
          <PdfNavigationBar
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            pdfScale={pdfScale}
            setPdfScale={setPdfScale}
            onFitToPage={handleFitToPage}
            // Canvas ref for dynamic width calculation
            canvasRef={canvasRef}
            // Grid Controls
            showGrid={showGrid}
            setShowGrid={setShowGrid}
            selectedTable={selectedTable}
            // Table Structure Controls
            isPlacingGridLine={isPlacingGridLine}
            placingGridLineType={placingGridLineType}
            cancelGridLinePlacement={cancelGridLinePlacement}
            startAddNewRow={startAddNewRow}
            startAddNewColumn={startAddNewColumn}
            isDraggingGridLine={isDraggingGridLine}
            // Drawing Tools
            drawingMode={drawingMode}
            setDrawingMode={setDrawingMode}
            showGridLineConfirmation={showGridLineConfirmation}
            setShowGridLineConfirmation={setShowGridLineConfirmation}
            cancelDrawing={cancelDrawing}
          />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            {isPdfLoading ? (
              <Typography>{"Loading PDF..."}</Typography>
            ) : isPdfError ? (
              <Typography color={"error"}>{"Error loading PDF"}</Typography>
            ) : (
              <Box
                ref={highlightContainerRef}
                sx={{ position: "relative", display: "inline-block" }}
              >
                <canvas
                  ref={canvasRef}
                  onMouseDown={(e) => {
                    if (isPlacingGridLine) {
                      handleGridLinePlacement(e);
                    } else if (drawingMode) {
                      startDrawing(e);
                    }
                  }}
                  onMouseMove={(e) => {
                    if (isDraggingGridLine) {
                      continueGridLineDrag(e);
                    } else {
                      continueDrawing(e);
                    }
                  }}
                  onMouseUp={(e) => {
                    if (isDraggingGridLine) {
                      stopGridLineDrag();
                    } else {
                      finishDrawing(e);
                    }
                  }}
                  onDoubleClick={finishPolygon}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isPlacingGridLine && !isDraggingGridLine) {
                        cancelDrawing();
                      }
                    }
                  }}
                  style={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: "8px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    backgroundColor: "white",
                    display: "block",
                    cursor: isPlacingGridLine
                      ? placingGridLineType === "row"
                        ? "ns-resize"
                        : "ew-resize"
                      : isDraggingGridLine
                      ? draggingGridLine?.type === "horizontal"
                        ? "ns-resize"
                        : "ew-resize"
                      : drawingMode
                      ? "crosshair"
                      : "default",
                  }}
                  tabIndex={0}
                />

                {/* Placement Mode Overlay */}
                {isPlacingGridLine && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                      border: "2px dashed #2196f3",
                      borderRadius: "8px",
                      p: 2,
                      textAlign: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography
                      variant={"body2"}
                      sx={{ fontWeight: "bold", color: "#1976d2" }}
                    >
                      {placingGridLineType === "row"
                        ? "Click where you want to add a new row"
                        : "Click where you want to add a new column"}
                    </Typography>
                    <Typography variant={"caption"} sx={{ color: "#666" }}>
                      {"Press Escape to cancel"}
                    </Typography>
                  </Box>
                )}

                {/* Dragging Mode Overlay */}
                {isDraggingGridLine && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 999,
                      backgroundColor: "rgba(255, 152, 0, 0.1)",
                      border: "2px dashed #ff9800",
                      borderRadius: "8px",
                      p: 2,
                      textAlign: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography
                      variant={"body2"}
                      sx={{ fontWeight: "bold", color: "#f57c00" }}
                    >
                      {"Drag to adjust position, release to confirm"}
                    </Typography>
                    <Typography variant={"caption"} sx={{ color: "#666" }}>
                      {"Press Escape to cancel"}
                    </Typography>
                  </Box>
                )}

                {/* PDF Cell Navigation Background */}
                {canvasRef.current && (
                  <svg
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents:
                        isPlacingGridLine || drawingMode ? "none" : "auto",
                      zIndex: 4, // Lower than grid lines
                    }}
                    viewBox={`0 0 ${canvasRef.current.width} ${canvasRef.current.height}`}
                  >
                    {/* Background rect for PDF-to-cell navigation */}
                    <rect
                      x={0}
                      y={0}
                      width={canvasRef.current.width}
                      height={canvasRef.current.height}
                      fill={"transparent"}
                      onMouseDown={(e) => {
                        if (handlePdfClick) {
                          handlePdfClick(e.nativeEvent);
                        }
                      }}
                    />
                  </svg>
                )}

                {/* Grid Lines - Optimized Component */}
                {showGrid && canvasRef.current && (
                  <OptimizedGridLines
                    gridLines={
                      dynamicGridLines?.[currentPage] ||
                      selectedTable?.grid_lines?.[currentPage]
                    }
                    currentPage={currentPage}
                    canvasRef={canvasRef}
                    pdfPageDimensions={pdfPageDimensions}
                    dynamicGridLines={dynamicGridLines}
                    gridLineDragMode={gridLineDragMode}
                    drawingMode={drawingMode}
                    isDraggingGridLine={isDraggingGridLine}
                    draggingGridLine={draggingGridLine}
                    isPlacingGridLine={isPlacingGridLine}
                    pendingNewGridLines={pendingNewGridLines}
                    handleGridLinePlacement={handleGridLinePlacement}
                    handlePdfClick={handlePdfClick}
                    startGridLineDrag={startGridLineDrag}
                    // Deletion indicators
                    selectedTable={selectedTable}
                    deletedRows={deletedRows}
                    deletedColumns={deletedColumns}
                  />
                )}
                {/* Word Highlights */}
                {highlights.map((highlight) => {
                  const colors = getPdfHighlightColor(highlight.isSelected);
                  return (
                    <Box
                      key={highlight.id}
                      sx={{
                        position: "absolute",
                        left: highlight.left,
                        top: highlight.top,
                        width: highlight.width,
                        height: highlight.height,
                        backgroundColor: colors.backgroundColor,
                        border: `2px solid ${colors.borderColor}`,
                        borderRadius: "3px",
                        pointerEvents: "none",
                        zIndex: highlight.isSelected ? 20 : 10,
                        boxShadow:
                          highlight.isSelected && colors.shadowColor !== "none"
                            ? `0 0 8px ${colors.shadowColor}`
                            : "none",
                        transition: "all 0.2s ease-out",
                      }}
                      title={`"${highlight.text}" (${(
                        (highlight.confidence || 0) * 100
                      ).toFixed(1)}% confidence)`}
                    />
                  );
                })}

                {/* Drawing Shape Preview */}
                {(drawingShape ||
                  (drawingMode === "polygon" && polygonPoints.length > 0)) &&
                  canvasRef.current && (
                    <svg
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 25,
                      }}
                      viewBox={`0 0 ${canvasRef.current.width} ${canvasRef.current.height}`}
                    >
                      {drawingShape && drawingShape.type === "rectangle" && (
                        <rect
                          x={
                            Math.min(
                              drawingShape.x,
                              drawingShape.x + drawingShape.width
                            ) *
                            (canvasRef.current.width /
                              pdfPageDimensions.current.width)
                          }
                          y={
                            Math.min(
                              drawingShape.y,
                              drawingShape.y + drawingShape.height
                            ) *
                            (canvasRef.current.height /
                              pdfPageDimensions.current.height)
                          }
                          width={
                            Math.abs(drawingShape.width) *
                            (canvasRef.current.width /
                              pdfPageDimensions.current.width)
                          }
                          height={
                            Math.abs(drawingShape.height) *
                            (canvasRef.current.height /
                              pdfPageDimensions.current.height)
                          }
                          fill={"rgba(76, 175, 80, 0.2)"}
                          stroke={"#4caf50"}
                          strokeWidth={"2"}
                          strokeDasharray={"5,5"}
                        />
                      )}
                      {drawingShape && drawingShape.type === "circle" && (
                        <circle
                          cx={
                            drawingShape.centerX *
                            (canvasRef.current.width /
                              pdfPageDimensions.current.width)
                          }
                          cy={
                            drawingShape.centerY *
                            (canvasRef.current.height /
                              pdfPageDimensions.current.height)
                          }
                          r={
                            drawingShape.radius *
                            (canvasRef.current.width /
                              pdfPageDimensions.current.width)
                          }
                          fill={"rgba(76, 175, 80, 0.2)"}
                          stroke={"#4caf50"}
                          strokeWidth={"2"}
                          strokeDasharray={"5,5"}
                        />
                      )}
                      {drawingShape &&
                        (drawingShape.type === "polygon" ||
                          drawingShape.type === "freehand") &&
                        drawingShape.points &&
                        drawingShape.points.length > 1 && (
                          <polygon
                            points={drawingShape.points
                              .map(
                                (p) =>
                                  `${
                                    p.x *
                                    (canvasRef.current.width /
                                      pdfPageDimensions.current.width)
                                  },${
                                    p.y *
                                    (canvasRef.current.height /
                                      pdfPageDimensions.current.height)
                                  }`
                              )
                              .join(" ")}
                            fill={"rgba(76, 175, 80, 0.2)"}
                            stroke={"#4caf50"}
                            strokeWidth={"2"}
                            strokeDasharray={"5,5"}
                          />
                        )}
                      {drawingMode === "polygon" && polygonPoints.length > 0 && (
                        <>
                          {polygonPoints.length > 1 && (
                            <polyline
                              points={polygonPoints
                                .map(
                                  (p) =>
                                    `${
                                      p.x *
                                      (canvasRef.current.width /
                                        pdfPageDimensions.current.width)
                                    },${
                                      p.y *
                                      (canvasRef.current.height /
                                        pdfPageDimensions.current.height)
                                    }`
                                )
                                .join(" ")}
                              fill={"none"}
                              stroke={"#4caf50"}
                              strokeWidth={"2"}
                              strokeDasharray={"5,5"}
                            />
                          )}
                          {polygonPoints.map((point, index) => (
                            <circle
                              key={index}
                              cx={
                                point.x *
                                (canvasRef.current.width /
                                  pdfPageDimensions.current.width)
                              }
                              cy={
                                point.y *
                                (canvasRef.current.height /
                                  pdfPageDimensions.current.height)
                              }
                              r={"4"}
                              fill={"#4caf50"}
                              stroke={"white"}
                              strokeWidth={"1"}
                            />
                          ))}
                        </>
                      )}
                    </svg>
                  )}

                {/* Preview Word Highlights for Drawing */}
                {(showDrawingConfirmation
                  ? pendingShapeWords
                  : previewWords
                ).map((shapeWord, index) => {
                  const word = shapeWord.word;
                  const bbox = word.bbox;
                  if (
                    !bbox ||
                    !canvasRef.current ||
                    !highlightContainerRef.current
                  )
                    return null;

                  const canvas = canvasRef.current;
                  const canvasRect = canvas.getBoundingClientRect();
                  const containerRect = highlightContainerRef.current.getBoundingClientRect();
                  const {
                    width: pdfWidth,
                    height: pdfHeight,
                  } = pdfPageDimensions.current;

                  const pdfToCanvasScaleX = canvas.width / pdfWidth;
                  const pdfToCanvasScaleY = canvas.height / pdfHeight;
                  const cssScaleX = canvasRect.width / canvas.width;
                  const cssScaleY = canvasRect.height / canvas.height;
                  const scaleX = pdfToCanvasScaleX * cssScaleX;
                  const scaleY = pdfToCanvasScaleY * cssScaleY;

                  let x0, y0, x1, y1;
                  if (
                    bbox.left !== undefined &&
                    bbox.top !== undefined &&
                    bbox.width !== undefined &&
                    bbox.height !== undefined
                  ) {
                    if (
                      bbox.left <= 1 &&
                      bbox.top <= 1 &&
                      bbox.width <= 1 &&
                      bbox.height <= 1
                    ) {
                      x0 = bbox.left * pdfWidth;
                      y0 = bbox.top * pdfHeight;
                      x1 = (bbox.left + bbox.width) * pdfWidth;
                      y1 = (bbox.top + bbox.height) * pdfHeight;
                    } else {
                      x0 = bbox.left;
                      y0 = bbox.top;
                      x1 = bbox.left + bbox.width;
                      y1 = bbox.top + bbox.height;
                    }
                  } else {
                    x0 = bbox.x0 || bbox.x || 0;
                    y0 = bbox.y0 || bbox.y || 0;
                    x1 = bbox.x1 || bbox.right || 0;
                    y1 = bbox.y1 || bbox.bottom || 0;

                    if (x0 <= 1 && y0 <= 1 && x1 <= 1 && y1 <= 1) {
                      x0 *= pdfWidth;
                      y0 *= pdfHeight;
                      x1 *= pdfWidth;
                      y1 *= pdfHeight;
                    }
                  }

                  const canvasX = x0 * scaleX;
                  const canvasY = y0 * scaleY;
                  const canvasWidth = (x1 - x0) * scaleX;
                  const canvasHeight = (y1 - y0) * scaleY;

                  const canvasOffsetX = canvasRect.left - containerRect.left;
                  const canvasOffsetY = canvasRect.top - containerRect.top;

                  return (
                    <Box
                      key={`preview-${index}`}
                      sx={{
                        position: "absolute",
                        left: canvasOffsetX + canvasX,
                        top: canvasOffsetY + canvasY,
                        width: canvasWidth,
                        height: canvasHeight,
                        backgroundColor: "rgba(76, 175, 80, 0.5)",
                        border: "2px solid #4caf50",
                        borderRadius: "2px",
                        pointerEvents: "none",
                        zIndex: 30,
                        boxShadow: "0 0 8px rgba(76, 175, 80, 0.6)",
                      }}
                      title={`Selected: "${word.text}"`}
                    />
                  );
                })}

                {/* Grid Line Moved Modal - Positioned below PDF canvas */}
                {showGridLineMoved && (
                  <Paper
                    elevation={3}
                    sx={{
                      position: "absolute",
                      top: 8, // Move higher up
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 10000,
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.5, sm: 0.625 }, // Reduced vertical padding for single line
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1.5, sm: 2 },
                      borderRadius: 0.75,
                      backdropFilter: "blur(8px)",
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      minWidth: { xs: "280px", sm: "320px" },
                      maxWidth: { xs: "90%", sm: "400px" },
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      height: "auto", // Ensure minimal height
                    }}
                  >
                    <Typography
                      variant={"body2"}
                      sx={{
                        fontWeight: "500",
                        color: "text.primary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" }, // Smaller text size
                        flex: 1,
                        textAlign: "center",
                        whiteSpace: "nowrap", // Ensure it stays in one line
                      }}
                    >
                      {"Grid line moved"}
                    </Typography>
                    <Button
                      size={"small"}
                      variant={"contained"}
                      disableElevation
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmGridLineMoved();
                      }}
                      sx={{
                        minWidth: { xs: "60px", sm: "80px" },
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.125, sm: 0.25 }, // Reduced vertical padding for compact buttons
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        fontWeight: 600,
                        height: { xs: "28px", sm: "32px" }, // Fixed height for consistency
                      }}
                    >
                      {"CONFIRM"}
                    </Button>
                    <Button
                      size={"small"}
                      variant={"contained"}
                      color={"secondary"}
                      disableElevation
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelGridLineMoved();
                      }}
                      sx={{
                        minWidth: { xs: "60px", sm: "80px" },
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.125, sm: 0.25 }, // Reduced vertical padding for compact buttons
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        fontWeight: 600,
                        height: { xs: "28px", sm: "32px" }, // Fixed height for consistency
                      }}
                    >
                      {"CANCEL"}
                    </Button>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default memo(PdfViewerPanel);
